import { useState, useEffect } from "react";
import Select from "react-select";
import { Icon } from "@iconify/react/dist/iconify.js";
import { toast } from "react-toastify";
import Modal from "../UI/Modal";
import Loader from "../UI/Loader";
import {
  getEstudiantesSinAsignarService,
  asignarEstudiantesService,
} from "../../services/seguimiento/seguimiento.service";

const AsignacionModal = ({ mentorId, onClose, onSuccess }) => {
  const [estudiantesOptions, setEstudiantesOptions] = useState([]);
  const [selectedEstudiantes, setSelectedEstudiantes] = useState([]);
  const [motivo, setMotivo] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [successInfo, setSuccessInfo] = useState(null);

  useEffect(() => {
    const fetchEstudiantes = async () => {
      setLoading(true);
      try {
        const data = await getEstudiantesSinAsignarService(mentorId);
        const options = data.map((est) => ({
          value: est.id,
          label: est.label,
        }));
        setEstudiantesOptions(options);
      } catch (error) {
        toast.error("Error al cargar los estudiantes sin asignar");
      } finally {
        setLoading(false);
      }
    };

    if (mentorId) {
      fetchEstudiantes();
    }
  }, [mentorId]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedEstudiantes || selectedEstudiantes.length === 0) {
      toast.warning("Debes seleccionar al menos un estudiante");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        mentor_id: mentorId,
        estudiantes_ids: selectedEstudiantes.map((opt) => opt.value),
        motivo: motivo,
      };

      const result = await asignarEstudiantesService(payload);

      if (result.total_errores > 0) {
        result.errores.forEach((err) => toast.error(err.error));
      }

      if (result.total_asignados > 0) {
        toast.success(
          `Se asignaron ${result.total_asignados} estudiante(s) correctamente`
        );
        setSuccessInfo({
          total: result.total_asignados,
          nombres: selectedEstudiantes.map((opt) => opt.label),
        });
      } else if (result.total_errores === 0) {
        onSuccess();
      }
    } catch (error) {
      toast.error("Error al realizar la asignación");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseSuccess = () => {
    setSuccessInfo(null);
    onSuccess();
  };

  return (
    <Modal
      open={true}
      onClose={successInfo ? handleCloseSuccess : onClose}
      title={successInfo ? "Asignación exitosa" : "Asignar Estudiantes"}
      size="md"
    >
      {loading ? (
        <Loader center type="dots" text="Cargando estudiantes..." />
      ) : successInfo ? (
        <div className="px-2 px-md-3 text-center py-3">
          <div
            className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
            style={{
              width: 72,
              height: 72,
              background: "rgba(16,185,129,0.15)",
              color: "#10b981",
            }}
          >
            <Icon icon="mdi:check-circle" width="48" />
          </div>
          <h5 className="mb-2">
            {successInfo.total === 1
              ? "1 estudiante asignado"
              : `${successInfo.total} estudiantes asignados`}
          </h5>
          <p className="text-secondary-light mb-3">
            La asignación se realizó correctamente al mentor seleccionado.
          </p>
          {successInfo.nombres?.length > 0 && (
            <div className="text-start mx-auto" style={{ maxWidth: 420 }}>
              <div className="small fw-semibold mb-2">Estudiantes:</div>
              <ul className="list-unstyled mb-0">
                {successInfo.nombres.map((n, i) => (
                  <li
                    key={i}
                    className="d-flex align-items-center gap-2 py-1"
                  >
                    <Icon
                      icon="mdi:account-check-outline"
                      width={18}
                      style={{ color: "#10b981" }}
                    />
                    <span className="text-sm">{n}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="d-flex justify-content-center mt-4 pt-3 border-top">
            <button
              type="button"
              className="btn btn-primary rounded-3 px-4"
              onClick={handleCloseSuccess}
              autoFocus
            >
              Continuar
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="px-2 px-md-3">
          <div className="mb-4">
            <label className="form-label fw-semibold small">
              Estudiantes (sin asignar) <span className="text-danger">*</span>
            </label>
            <Select
              isMulti
              options={estudiantesOptions}
              value={selectedEstudiantes}
              onChange={setSelectedEstudiantes}
              placeholder="Selecciona estudiantes..."
              className="react-select"
              noOptionsMessage={() => "No hay estudiantes sin asignar disponibles"}
            />
            <div className="form-text mt-2">
              Solo se muestran estudiantes de la misma escuela y plantel que el
              mentor.
            </div>
          </div>

          <div className="mb-4">
            <label className="form-label fw-semibold small">Motivo (Opcional)</label>
            <textarea
              className="form-control shadow-sm rounded-3"
              rows="3"
              placeholder="Ej: Reorganización de grupos"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
            ></textarea>
          </div>

          <div className="d-flex justify-content-end gap-3 mt-4 pt-3 border-top">
            <button
              type="button"
              className="btn btn-light border rounded-3 px-4"
              onClick={onClose}
              disabled={submitting}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary rounded-3 px-4 shadow-sm"
              disabled={submitting || selectedEstudiantes.length === 0}
            >
              {submitting ? "Guardando..." : "Guardar Asignación"}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default AsignacionModal;
